import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ComponentType } from 'react'
import { compile } from './compile'
import { ExampleErrorBoundary } from './ErrorBoundary'
import styles from './Workspace.module.css'

export interface WorkspaceProps {
  /** Starting source. Also what "Reset" restores. */
  source: string
  /** Identifies the example in tests and in the editor label. */
  name: string
}

interface Rendered {
  Component: ComponentType
  /** Bumped per successful compile so the error boundary retries. */
  key: number
}

/**
 * A live example: the rendered component over a grid backdrop, with its source
 * in a textarea below. Editing re-renders.
 *
 * The last good render is deliberately kept on screen when an edit fails, so a
 * reader who breaks the snippet mid-keystroke still sees what they had and can
 * simply undo. Blanking the stage on every transient parse error would make the
 * editor unusable.
 */
export function Workspace({ source, name }: WorkspaceProps) {
  const [text, setText] = useState(source)

  /*
   * Re-seed when the page changes.
   *
   * `useState(source)` reads `source` on the first render only. Navigating
   * between component pages renders a Workspace at the same position in the
   * tree, so React keeps the existing instance and its state — the editor kept
   * showing the *previous* page's snippet until the reader hit Reset, which is
   * the one control that calls setText(source) again.
   *
   * Adjusting state during render is React's documented fix for "reset state
   * when a prop changes"; it re-renders immediately, before anything paints, so
   * the stale source is never shown.
   */
  const [seeded, setSeeded] = useState(source)
  if (seeded !== source) {
    setSeeded(source)
    setText(source)
  }
  const [rendered, setRendered] = useState<Rendered | null>(null)
  const [error, setError] = useState<{ stage: string; message: string } | null>(
    null
  )
  const version = useRef(0)

  useEffect(() => {
    const result = compile(text)
    if (result.ok) {
      version.current += 1
      setRendered({ Component: result.Component, key: version.current })
      setError(null)
    } else {
      setError({ stage: result.stage, message: result.message })
    }
  }, [text])

  const onRuntimeError = useCallback((message: string) => {
    setError({ stage: 'render', message })
  }, [])

  const dirty = text !== source
  const reset = useCallback(() => setText(source), [source])

  const Example = rendered?.Component
  const showingStale = error !== null && rendered !== null

  const editorId = useMemo(
    () => `workspace-editor-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
    [name]
  )

  return (
    <section
      className={styles.workspace}
      data-testid={`workspace-${name}`}
      aria-label={`${name} live example`}
    >
      <div className={styles.stage} data-testid="workspace-stage">
        <div className={styles.stageInner}>
          {Example && (
            <ExampleErrorBoundary
              resetKey={rendered?.key}
              onError={onRuntimeError}
            >
              <Example />
            </ExampleErrorBoundary>
          )}
        </div>
      </div>

      <div className={styles.editorHead}>
        <label className={styles.editorLabel} htmlFor={editorId}>
          {name} example
        </label>
        <button
          type="button"
          className={styles.reset}
          onClick={reset}
          disabled={!dirty}
          data-testid="workspace-reset"
        >
          Reset
        </button>
      </div>

      <textarea
        id={editorId}
        className={styles.editor}
        value={text}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
        autoCapitalize="off"
        autoCorrect="off"
        rows={Math.min(20, Math.max(6, text.split('\n').length + 1))}
        data-testid="workspace-editor"
        aria-describedby={error ? `${editorId}-error` : undefined}
      />

      {error && (
        <p
          className={styles.error}
          id={`${editorId}-error`}
          role="alert"
          data-testid="workspace-error"
        >
          <span className={styles.errorStage}>{error.stage}</span>
          <span data-testid="workspace-error-message">{error.message}</span>
        </p>
      )}
      {showingStale && (
        <p className={styles.stale} data-testid="workspace-stale">
          Showing the last version that compiled. Undo the edit to get it back.
        </p>
      )}
    </section>
  )
}
